import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Field, ErrorMessage } from '@scoreboard/ui-web';
import { errorMessage } from '@/lib/errors';
import { useSession } from '@/lib/session';
import i18n from '@/lib/i18n';
import type { Locale } from '@scoreboard/shared';

// Magic link (doc 02 §2.1) : l'API répond toujours 202 (anti-énumération), donc
// l'écran « lien envoyé » ne dit jamais si l'email est connu.

export default function LoginRoute(): React.ReactElement {
  const { t } = useTranslation();
  const { api, status } = useSession();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to="/games" replace />;
  }

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setPending(true);
    try {
      await api.requestMagicLink(email.trim(), i18n.language as Locale);
      setSent(true);
    } catch (err) {
      setError(errorMessage(t, err));
    } finally {
      setPending(false);
    }
  };

  if (sent) {
    return (
      <div className="page page--narrow stack">
        <h1>{t('auth.sentTitle')}</h1>
        <p className="muted">{t('auth.sentBody')}</p>
        <button type="button" className="btn" onClick={() => setSent(false)}>
          {t('auth.sentAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="page page--narrow stack">
      <h1>{t('auth.title')}</h1>
      <p className="muted">{t('auth.intro')}</p>
      <form className="stack" onSubmit={(e) => void onSubmit(e)} noValidate>
        <Field
          label={t('auth.email')}
          hint={t('auth.emailHint')}
          error={invalid ? t('auth.invalidEmail') : undefined}
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        {error !== null ? <ErrorMessage message={error} /> : null}
        <button type="submit" className="btn btn--primary btn--lg" disabled={pending}>
          {pending ? t('auth.sending') : t('auth.send')}
        </button>
      </form>
    </div>
  );
}
